import React, { useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  DescriptionOutlined,
  ErrorOutlineOutlined,
  InfoOutlined,
  ListAltOutlined,
  PriorityHighRounded,
  QuestionMarkRounded,
  ReplayRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { pfValidationHeader } from "pages/PayRoll/PayRollHeaders";
import { panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { RecordHeaderCard, StatCardGrid, StatutoryList, ToolbarAddButton, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { SEVERITY_COLORS } from "./pfReportsData";

// Validation (P12) and the Validation Issue drawer (P12a). Checks run by
// themselves when payroll is finalised and when PF details, exits or codes
// change; Re-check now runs them again. Blocking issues stop the ECR file
// for that code; warnings and info do not.

const ALL = "all";
const { pink, orange, blue, green } = detailAccentPalette;
const FIX_PATHS = {
  "PF Members": "/admin/payroll/statutory/pf",
  "Employee profile": "/admin/hrzone/employees",
  "Exit Codes": "/admin/payroll/configuration/statutory-configuration?item=pf&tab=exit-codes",
};
const FIX_LABELS = { "PF Members": "Open PF Members", "Employee profile": "Open Employee", "Exit Codes": "Open Exit Codes" };

const IssuePanel = ({ open, issue, onClose }) => {
  const history = useHistory();
  if (!issue) return null;
  const path = FIX_PATHS[issue.fix_in];
  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Validation Issue"
      subtitle={`${issue.severity} · ${issue.affects === "—" ? "No effect on files" : issue.affects}`}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
          {path ? <PrimaryButton onClick={() => history.push(path)}>{FIX_LABELS[issue.fix_in]}</PrimaryButton> : null}
        </Mui.Stack>
      }
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar={initialsOf(issue.name)}
          title={issue.name}
          chip={<RequestStatusChip status={issue.severity} colorCode={SEVERITY_COLORS[issue.severity]} />}
          meta={`${issue.emp_id} · UAN ${issue.uan || "not yet generated"} · ${issue.code_label} code`}
          tone={issue.severity === "Blocking" ? "danger" : issue.severity === "Warning" ? "warning" : "teal"}
        />
        <RequestDetailsSection
          title="ISSUE"
          fields={[
            { icon: PriorityHighRounded, label: "Problem", value: issue.issue.split(" — ")[0], accent: pink, multiline: true },
            { icon: QuestionMarkRounded, label: "Why it matters", value: issue.why, accent: orange, multiline: true },
            { icon: DescriptionOutlined, label: "Effect", value: issue.effect, accent: blue, multiline: true },
          ]}
        />
        <RequestDetailsSection
          title="HOW TO FIX"
          fields={issue.steps.map(([title, detail], index) => ({
            icon: () => (
              <Mui.Box component="span" sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>
                {index + 1}
              </Mui.Box>
            ),
            label: title,
            value: detail,
            accent: green,
            multiline: true,
          }))}
        />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

export const PfValidationTab = ({ issues, lastCheck, onRecheck }) => {
  const { domain, userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [card, setCard] = useState(ALL);
  const [viewId, setViewId] = useState(null);
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "pf_validation_saved_views",
    fullHeaders: pfValidationHeader,
  });

  const rows = card === ALL ? issues : issues.filter((issue) => issue.severity === card);
  const count = (severity) => issues.filter((issue) => issue.severity === severity).length;

  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>{initialsOf(row.name)}</Mui.Avatar>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          </Mui.Stack>
        );
      case "severity":
        return <RequestStatusChip status={row.severity} colorCode={SEVERITY_COLORS[row.severity]} />;
      default:
        return row[key] || "—";
    }
  };

  return (
    <>
      <StatCardGrid
        value={card}
        onChange={setCard}
        cards={[
          { key: ALL, title: "All issues", value: issues.length, icon: ListAltOutlined, tone: "blue" },
          { key: "Blocking", title: "Blocking", value: count("Blocking"), icon: ErrorOutlineOutlined, tone: "red" },
          { key: "Warning", title: "Warning", value: count("Warning"), icon: WarningAmberRounded, tone: "amber" },
          { key: "Info", title: "Info", value: count("Info"), icon: InfoOutlined, tone: "green" },
        ]}
      />
      <Mui.Typography sx={{ ...designType.captionText, mb: 1 }}>
        Last checked {moment(lastCheck.at).format("DD MMM YYYY, HH:mm")} · trigger: {lastCheck.trigger}
      </Mui.Typography>
      <StatutoryList
        title="PF Validation"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={rows}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="issues"
        searchPlaceholder="Search"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
        action={
          <ToolbarAddButton
            startIcon={<ReplayRounded />}
            onClick={() => {
              onRecheck();
              showToast("success", "Validation checked again.");
            }}
          >
            Re-check now
          </ToolbarAddButton>
        }
      />
      <Mui.Box sx={{ mt: 1.5 }}>
        <RequestInfoBanner body="Checks run by themselves when payroll is finalised and when PF details, exits or codes change. Use Re-check now after fixing something. Blocking issues stop the ECR file for that code; warnings and info do not." />
      </Mui.Box>
      <IssuePanel open={Boolean(viewId)} issue={issues.find((issue) => issue.id === viewId)} onClose={() => setViewId(null)} />
    </>
  );
};
